import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Users, Check } from "lucide-react";
import { toast } from "sonner";

export default function MeetupsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meetups, setMeetups] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  const load = () => api.get("/meetups").then((r) => setMeetups(r.data || []));
  useEffect(() => {
    load();
  }, [user]);

  const rsvp = async (id) => {
    if (!user) {
      toast("Sign in to RSVP");
      navigate("/login");
      return;
    }
    setLoadingId(id);
    try {
      const { data } = await api.post(`/meetups/${id}/rsvp`);
      toast(data.rsvped ? "You're in. See you there." : "RSVP cancelled.");
      load();
    } catch (e) {
      toast("Could not update RSVP");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-12" data-testid="meetups-page">
      <div className="overline">In real life</div>
      <h1 className="font-display text-5xl md:text-6xl uppercase mt-3">City meetups</h1>
      <p className="text-white/70 mt-4 max-w-2xl">
        Pull up. Coffee, hikes, sunset jams. RSVP locks your spot — capacity is real.
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {meetups.map((m) => {
          const dt = new Date(m.starts_at);
          return (
            <article
              key={m.id}
              className="tac-card overflow-hidden flex flex-col"
              data-testid={`meetup-${m.id}`}
            >
              {m.image_url && (
                <div
                  className="aspect-[16/10] bg-cover bg-center"
                  style={{ backgroundImage: `url(${m.image_url})` }}
                />
              )}
              <div className="p-6 flex-1 flex flex-col">
                <div className="overline">
                  {m.city}, {m.state}
                </div>
                <h3 className="font-display text-3xl uppercase mt-2">{m.title}</h3>
                <p className="text-white/70 text-sm mt-3 line-clamp-3">{m.description}</p>

                <div className="mt-5 space-y-2 text-xs text-white/70 font-mono-tactical">
                  <div className="flex items-center gap-2">
                    <Calendar size={12} className="text-[#FF1493]" />
                    {dt.toLocaleDateString(undefined, {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                    {" · "}
                    {dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={12} className="text-[#FF1493]" />
                    {m.venue}
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={12} className="text-[#FF1493]" />
                    {m.attendees}/{m.capacity} attending
                  </div>
                </div>

                <button
                  onClick={() => rsvp(m.id)}
                  disabled={loadingId === m.id}
                  data-testid={`rsvp-${m.id}`}
                  className={m.rsvped ? "btn-ghost mt-6" : "btn-blaze mt-6"}
                >
                  {m.rsvped ? (
                    <>
                      <Check size={14} /> You're in · Tap to cancel
                    </>
                  ) : (
                    <>RSVP</>
                  )}
                </button>
              </div>
            </article>
          );
        })}
      </div>
      {meetups.length === 0 && (
        <div className="text-white/60 text-center py-20">No meetups scheduled. Check back soon.</div>
      )}
    </div>
  );
}
